'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';

// Types of changes that can occur in the application
export type ChangeType = 'xml' | 'canvas' | 'file' | 'form';
export type ChangeAction = 'add' | 'modify' | 'delete';

// Represents a single change event
export interface Change {
  id: string;
  timestamp: Date;
  type: ChangeType;
  action: ChangeAction;
  // For XML changes
  elementType?: string; // 'StudyEventDef', 'FormDef', 'ItemDef', etc.
  oid?: string; // OID of changed element
  xpath?: string; // XPath to element in XML
  xmlLineStart?: number; // Line number in formatted XML
  xmlLineEnd?: number; // End line number
  // For canvas changes
  sectionId?: string; // Anchor ID in canvas (e.g., 'section-assistant_xxx')
  // Common fields
  summary: string; // Human-readable description
  details?: string; // Additional details for tooltip/expansion
  seen: boolean; // Has user acknowledged/seen this change?
  sourceMessageId?: string; // ID of the chat message that caused this change
}

// Input for adding a new change (id, timestamp, seen auto-generated)
export type NewChange = Omit<Change, 'id' | 'timestamp' | 'seen'>;

// Context value interface
interface ChangeContextValue {
  // State
  changes: Change[];
  unseenCount: number;

  // Actions
  addChange: (change: NewChange) => string; // Returns the change ID
  addChanges: (changes: NewChange[]) => string[]; // Batch add, returns IDs
  markSeen: (changeId: string) => void;
  markAllSeen: (type?: ChangeType) => void;
  clearChanges: (type?: ChangeType) => void;

  // Queries
  getChangesForTab: (tabType: 'xml' | 'form' | 'graph' | 'canvas') => Change[];
  getUnseenCountForTab: (tabType: 'xml' | 'form' | 'graph' | 'canvas') => number;
  getChangeById: (changeId: string) => Change | undefined;
  getChangesByMessageId: (messageId: string) => Change[];

  // Navigation helpers
  jumpToChange: (changeId: string) => void;
}

// Create context with undefined default (must be used within provider)
const ChangeContext = createContext<ChangeContextValue | undefined>(undefined);

// Hook to use the change context
export function useChangeContext() {
  const context = useContext(ChangeContext);
  if (!context) {
    throw new Error("useChangeContext must be used within a ChangeProvider");
  }
  return context;
}

// Optional hook that doesn't throw (for components that may be outside provider)
export function useChangeContextOptional() {
  return useContext(ChangeContext);
}

// Generate unique change ID
let changeIdCounter = 0;
function generateChangeId(): string {
  changeIdCounter++;
  return `change_${Date.now()}_${changeIdCounter}`;
}

// Provider props
interface ChangeProviderProps {
  children: ReactNode;
  onJumpToChange?: (change: Change) => void;
}

// Provider component
export function ChangeProvider(t0) {
  const $ = _c(30);
  const {
    children,
    onJumpToChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [changes, setChanges] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = newChange => {
      const id = generateChangeId();
      const change = {
        ...newChange,
        id,
        timestamp: new Date(),
        seen: false
      };
      setChanges(prev => [...prev, change]);
      return id;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const addChange = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = newChanges => {
      const ids = [];
      const changesToAdd = newChanges.map(nc => {
        const id_0 = generateChangeId();
        ids.push(id_0);
        return {
          ...nc,
          id: id_0,
          timestamp: new Date(),
          seen: false
        };
      });
      setChanges(prev_0 => [...prev_0, ...changesToAdd]);
      return ids;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const addChanges = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = changeId => {
      setChanges(prev_1 => prev_1.map(c => c.id === changeId ? {
        ...c,
        seen: true
      } : c));
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const markSeen = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = type => {
      setChanges(prev_2 => prev_2.map(c_0 => type === undefined || c_0.type === type ? {
        ...c_0,
        seen: true
      } : c_0));
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const markAllSeen = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = type_0 => {
      setChanges(prev_3 => type_0 === undefined ? [] : prev_3.filter(c_1 => c_1.type !== type_0));
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const clearChanges = t6;
  let t7;
  if ($[6] !== changes) {
    t7 = tabType => {
      const tabToChangeTypes = {
        "xml": ["xml"],
        "form": ["xml", "form"],
        "graph": ["xml"],
        "canvas": ["canvas"]
      };
      const relevantTypes = tabToChangeTypes[tabType] || [];
      return changes.filter(c_2 => relevantTypes.includes(c_2.type));
    };
    $[6] = changes;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const getChangesForTab = t7;
  let t8;
  if ($[8] !== changes) {
    t8 = tabType_0 => {
      const tabToChangeTypes_0 = {
        "xml": ["xml"],
        "form": ["xml", "form"],
        "graph": ["xml"],
        "canvas": ["canvas"]
      };
      const relevantTypes_0 = tabToChangeTypes_0[tabType_0] || [];
      return changes.filter(c_3 => relevantTypes_0.includes(c_3.type) && !c_3.seen).length;
    };
    $[8] = changes;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const getUnseenCountForTab = t8;
  let t9;
  if ($[10] !== changes) {
    t9 = changeId_0 => changes.find(c_4 => c_4.id === changeId_0);
    $[10] = changes;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const getChangeById = t9;
  let t10;
  if ($[12] !== changes) {
    t10 = messageId => changes.filter(c_5 => c_5.sourceMessageId === messageId);
    $[12] = changes;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const getChangesByMessageId = t10;
  let t11;
  if ($[14] !== changes || $[15] !== onJumpToChange) {
    t11 = changeId_1 => {
      const change_0 = changes.find(c_6 => c_6.id === changeId_1);
      if (change_0) {
        markSeen(changeId_1);
        onJumpToChange?.(change_0);
      }
    };
    $[14] = changes;
    $[15] = onJumpToChange;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const jumpToChange = t11;
  let t12;
  if ($[17] !== changes) {
    t12 = changes.filter(_temp);
    $[17] = changes;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const unseenCount = t12.length;
  let t13;
  if ($[19] !== changes || $[20] !== getChangeById || $[21] !== getChangesByMessageId || $[22] !== getChangesForTab || $[23] !== getUnseenCountForTab || $[24] !== jumpToChange || $[25] !== unseenCount) {
    t13 = {
      changes,
      unseenCount,
      addChange,
      addChanges,
      markSeen,
      markAllSeen,
      clearChanges,
      getChangesForTab,
      getUnseenCountForTab,
      getChangeById,
      getChangesByMessageId,
      jumpToChange
    };
    $[19] = changes;
    $[20] = getChangeById;
    $[21] = getChangesByMessageId;
    $[22] = getChangesForTab;
    $[23] = getUnseenCountForTab;
    $[24] = jumpToChange;
    $[25] = unseenCount;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const value = t13;
  let t14;
  if ($[27] !== children || $[28] !== value) {
    t14 = <ChangeContext.Provider value={value}>{children}</ChangeContext.Provider>;
    $[27] = children;
    $[28] = value;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function _temp(c_7) {
  return !c_7.seen;
}